<template>
  <div class="list-page-container">
    <!-- 顶部操作区：副标题与创建按钮 -->
    <div class="control-panel">
      <div class="control-container">
        <div class="header-info">
          <i class="el-icon-cpu"></i>
          <span>{{ $t('device.deviceListTips') }}</span>
        </div>
        <div class="action-wrapper">
          <el-button type="primary" @click="handleBindDevice" v-if="hasPermission('sys:device:update')">
            <i class="el-icon-link"></i> 绑定设备
          </el-button>
        </div>
      </div>
    </div>

    <!-- 统一控制中心：筛选模块 -->
    <div class="control-panel has-query">
      <div class="control-container">
        <!-- 搜索筛选与主要操作区 -->
        <el-form :inline="true" :model="filterForm" class="filter-form">
          <el-form-item>
            <el-input
              v-model="filterForm.keywords"
              :placeholder="$t('device.deviceNamePlaceholder')"
              clearable
              class="filter-input"
              @keyup.enter.native="handleSearch"
              @clear="handleSearch"
            >
              <i slot="prefix" class="el-input__icon el-icon-search"></i>
            </el-input>
          </el-form-item>
          <el-form-item>
            <el-input
              v-model="filterForm.deviceCode"
              :placeholder="$t('device.deviceCodeFilterPlaceholder')"
              clearable
              class="filter-input"
              @keyup.enter.native="handleSearch"
              @clear="handleSearch"
              @input="handleDeviceCodeInput"
            >
              <i slot="prefix" class="el-input__icon el-icon-aim"></i>
            </el-input>
          </el-form-item>
          <el-form-item>
            <el-select
              v-model="filterForm.category"
              :placeholder="$t('userManagement.filterByType') || '设备种类'"
              clearable
              class="filter-select"
              @change="handleSearch"
            >
              <i slot="prefix" class="el-input__icon el-icon-s-operation"></i>
              <el-option
                v-for="item in deviceTypes"
                :key="item.key"
                :label="item.name"
                :value="item.key"
              ></el-option>
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-select
              v-model="filterForm.board"
              :placeholder="$t('userManagement.filterByModel')"
              clearable
              class="filter-select"
              @change="handleSearch"
            >
              <i slot="prefix" class="el-input__icon el-icon-cpu"></i>
              <el-option
                v-for="item in firmwareTypes"
                :key="item.key"
                :label="item.name"
                :value="item.key">
              </el-option>
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-select
              v-model="filterForm.status"
              :placeholder="$t('userManagement.filterByStatus')"
              clearable
              class="filter-select"
              @change="handleSearch"
            >
              <i slot="prefix" class="el-input__icon el-icon-connection"></i>
              <el-option :label="$t('common.online')" :value="1"></el-option>
              <el-option :label="$t('common.offline')" :value="0"></el-option>
            </el-select>
          </el-form-item>
          <div class="search-btns">
            <el-button type="primary" @click="handleSearch" icon="el-icon-search">{{ $t('common.search') }}</el-button>
            <el-button @click="handleReset" icon="el-icon-refresh">{{ $t('common.reset') }}</el-button>
          </div>
        </el-form>

        <!-- 批量操作与工具区 -->
        <div class="action-wrapper">
          <div class="left-actions">
            <el-button-group class="batch-group">
              <el-button
                type="warning"
                @click="handleBatchOperation"
                :disabled="!selectedDevices.length"
                plain
                size="small"
                v-if="hasPermission('sys:device:update')"
              >
                <i class="el-icon-s-cooperation"></i> {{ $t('common.batchOperation') }}
              </el-button>
            </el-button-group>
          </div>
          <div class="right-tools">
            <el-button @click="handleRefresh" icon="el-icon-refresh" circle size="small" :title="$t('common.refresh')"></el-button>
            <el-button type="info" @click="handleExport" :loading="exportLoading" icon="el-icon-download" circle size="small" :title="$t('common.export')" v-if="hasPermission('sys:device:export')"></el-button>
          </div>
        </div>
      </div>
    </div>
    
    <el-card class="list-card">
      <el-table
        :data="deviceList"
        v-loading="loading"
        height="100%"
        :element-loading-text="$t('common.loading')"
        @selection-change="handleSelectionChange"
        @header-dragend="handleHeaderDragend"
        class="custom-table"
        style="width: 100%"
      >
        <el-table-column type="selection" width="55" align="center"></el-table-column>
        
        <el-table-column 
          prop="deviceName" 
          :label="$t('device.deviceName')" 
          :width="getColumnWidth('deviceName', 250)"
          min-width="120"
          show-overflow-tooltip
          align="center"
        >
          <template slot-scope="scope">
            <span class="device-name-link" @click="handleViewDevice(scope.row)">{{ scope.row.deviceName }}</span>
          </template>
        </el-table-column>

        <el-table-column 
          prop="deviceCode" 
          :label="$t('device.deviceCode')" 
          :width="getColumnWidth('deviceCode', 250)"
          min-width="140"
          show-overflow-tooltip
          align="center"
        >
          <template slot-scope="scope">
            <code class="device-code">{{ scope.row.deviceCode }}</code>
          </template>
        </el-table-column>

        <el-table-column 
          prop="productType"
          :label="$t('device.deviceType')" 
          :width="getColumnWidth('productType', 120)"
          min-width="100" 
          align="center"
        >
          <template slot-scope="scope">
            <el-tag 
              size="small" 
              effect="plain"
              :type="scope.row.productType === 'DEVICE' ? 'primary' : 'success'"
            >
              {{ scope.row.productType === 'DEVICE' ? $t('device.smartDevice') : $t('device.peripheral') }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column 
          prop="board" 
          :label="$t('device.deviceModel')" 
          :width="getColumnWidth('board', 250)"
          min-width="100" 
          align="center"
        >
          <template slot-scope="scope">
            <el-tag size="small" effect="plain" v-if="scope.row.productType === 'DEVICE'">
              {{ scope.row.boardLabel || getDeviceModelLabel(scope.row.board) }}
            </el-tag>
            <el-tag size="small" effect="plain" type="success" v-else-if="scope.row.productType === 'PERIPHERAL'">
              {{ getPeripheralTypeLabel(scope.row.deviceType) }}
            </el-tag>
            <span v-else>-</span>
          </template>
        </el-table-column>

        <el-table-column
          prop="status"
          :label="$t('common.status')"
          :width="getColumnWidth('status', 120)"
          min-width="80"
          align="center"
        >
          <template slot-scope="scope">
            <el-badge is-dot :type="scope.row.status ? 'success' : 'danger'" class="status-badge">
              <el-tag :type="scope.row.status ? 'success' : 'danger'" size="small">
                {{ scope.row.status ? $t('common.online') : $t('common.offline') }}
              </el-tag>
            </el-badge>
          </template>
        </el-table-column>

        <el-table-column 
          prop="bindUserName"
          :label="$t('device.bindUser')" 
          :width="getColumnWidth('bindUserName', 120)"
          min-width="100" 
          align="center"
        >
          <template slot-scope="scope">
            <span v-if="scope.row.bindUserName" class="bind-username">{{ scope.row.bindUserName }}</span>
            <span v-else class="empty-text">-</span>
          </template>
        </el-table-column>

          <el-table-column 
            prop="appVersion" 
            :label="$t('device.version')" 
            :width="getColumnWidth('appVersion', 120)"
            min-width="90"
            align="center"
          >
            <template slot-scope="scope">
              <span class="version-tag">{{ scope.row.appVersion || '-' }}</span>
            </template>
          </el-table-column>

          <el-table-column
            prop="autoUpdate" 
          :label="$t('device.autoUpdate')" 
          :width="getColumnWidth('autoUpdate', 120)"
          min-width="100" 
          align="center"
        >
          <template slot-scope="scope">
            <el-switch
              v-model="scope.row.autoUpdate"
              :active-value="1"
              :inactive-value="0"
              size="small"
              @change="(val) => handleAutoUpdateChange(scope.row, val)"
            >
            </el-switch>
          </template>
        </el-table-column>

          <el-table-column
            prop="activationTime" 
          :label="$t('device.activationTime')" 
          :width="getColumnWidth('activationTime', 160)"
          min-width="150"
          align="center"
        >
          <template slot-scope="scope">
            <span class="time-text">{{ scope.row.activationTime ? formatDateTime(scope.row.activationTime) : '-' }}</span>
          </template>
        </el-table-column>

        <el-table-column 
          prop="lastConnectedAt" 
          :label="$t('device.lastConversation')" 
          min-width="150"
          align="center"
        >
          <template slot-scope="scope">
            <span class="time-text">{{ scope.row.lastConnectedAt ? formatDateTime(scope.row.lastConnectedAt) : '-' }}</span>
          </template>
        </el-table-column>

        <el-table-column v-if="hasAnyActionPermission" :label="$t('common.operation')" :width="actionColumnWidth" align="center" fixed="right">
          <template slot-scope="scope">
            <div class="action-buttons">
              <el-button
                size="mini"
                type="primary"
                plain
                @click="handleViewDevice(scope.row)"
                icon="el-icon-view"
                :aria-label="$t('common.detail')"
                v-if="hasPermission('sys:device:info')"
              >
                {{ $t('common.detail') }}
              </el-button>
              <el-button
                 size="mini"
                 type="warning"
                 plain
                 @click="handleEditDevice(scope.row)"
                 icon="el-icon-edit"
                 :aria-label="$t('common.edit')"
                 v-if="hasPermission('sys:device:update')"
               >
                 {{ $t('common.edit') }}
               </el-button>
               <el-button
                 size="mini"
                 type="danger"
                 plain
                 @click="handleUnbindDevice(scope.row)"
                 icon="el-icon-delete"
                 v-if="hasPermission('sys:device:update')"
                 :aria-label="'解绑'"
               >
                 解绑
               </el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>
      
      <!-- 分页控件 -->
      <div class="pagination-container">
        <el-pagination
          :current-page="page"
          :page-size="limit"
          :page-sizes="[10, 20, 50, 100]"
          :total="total"
          layout="total, sizes, prev, pager, next, jumper"
          @current-change="handleCurrentChange"
          @size-change="handleSizeChange"
        ></el-pagination>
      </div>
    </el-card>

    <!-- 底部悬浮批量操作栏 -->
    <transition name="el-zoom-in-bottom">
      <div v-if="selectedDevices.length > 0" class="batch-footer-bar">
        <div class="batch-info">
          <i class="el-icon-info"></i>
          <span>已选择 {{ selectedDevices.length }} 项</span>
        </div>
        <div class="batch-buttons">
          <el-button type="warning" size="small" icon="el-icon-s-cooperation" @click="handleBatchOperation" v-if="hasPermission('sys:device:update')">{{ $t('common.batchOperation') }}</el-button>
          <el-divider direction="vertical" v-if="hasPermission('sys:device:update')"></el-divider>
          <el-button type="text" @click="selectedDevices = []">{{ $t('common.cancel') }}</el-button>
        </div>
      </div>
    </transition>

    <!-- 批量操作对话框 -->
    <el-dialog
      :title="$t('device.batchOperation')"
      :visible.sync="batchOperationDialog.visible"
      width="500px"
      :close-on-click-modal="false"
    >
      <div class="batch-operation-content">
        <p>{{ $t('device.batchOperationDesc', { count: selectedDevices.length }) }}</p>
        
        <el-form :model="batchForm" label-width="100px">
          <el-form-item :label="$t('device.operationType')">
            <el-select v-model="batchForm.operation" :placeholder="$t('device.selectOperation')">
              <el-option :label="$t('device.enableDevice')" value="enable"></el-option>
              <el-option :label="$t('device.disableDevice')" value="disable"></el-option>
              <el-option :label="$t('device.deleteDevice')" value="delete"></el-option>
              <el-option :label="$t('device.bindDevice')" value="bind"></el-option>
              <el-option :label="$t('device.unbindDevice')" value="unbind"></el-option>
            </el-select>
          </el-form-item>
          
          <el-form-item v-if="batchForm.operation === 'bind'" :label="$t('device.bindUser')">
            <el-select v-model="batchForm.userId" :placeholder="$t('device.selectUser')" filterable>
              <el-option v-for="(user, index) in userList" :key="`${user.id}-${index}`" :label="user.username || user.name" :value="user.id"></el-option>
            </el-select>
          </el-form-item>
        </el-form>
      </div>
      
      <div slot="footer" class="dialog-footer">
        <el-button @click="batchOperationDialog.visible = false">{{ $t('common.cancel') }}</el-button>
        <el-button type="primary" @click="executeBatchOperation" :loading="batchOperationDialog.loading">
          {{ $t('common.confirm') }}
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { hasPermission } from '@/utils/permission'
import { getDeviceList, batchDeviceOperation, updateDevice, unbindDeviceByCode } from '@/apis/device/device'
import { getUserList } from '@/apis/user/user'
import { getDictDataByType } from '@/apis/system/dict'
import { debounce } from '@/utils/debounce'

export default {
  name: 'DeviceList',
  data() {
    return {
      loading: false,
      exportLoading: false,
      deviceList: [],
      selectedDevices: [],
      userList: [],
      filterForm: {
        keywords: '',
        deviceCode: '',
        board: '',
        status: '',
        category: '',
        productType: 'DEVICE'
      },
      page: 1,
      limit: 10,
      total: 0,
      firmwareTypes: [],
      deviceTypes: [], // 产品类型 (PRODUCT_TYPE)
      peripheralTypes: [], // 周边类型 (DEVICE_TYPE)
      batchOperationDialog: {
        visible: false,
        loading: false
      },
      batchForm: {
        operation: '',
        userId: ''
      },
      // 列宽持久化配置
      columnWidths: {}
    }
  },
  computed: {
    // 操作列权限判断
    canViewDevice() {
      return this.hasPermission('sys:device:info')
    },
    canEditDevice() {
      return this.hasPermission('sys:device:update')
    },
    // 操作列可见按钮数量（基于权限）
    visibleActionCount() {
      let count = 0
      if (this.canViewDevice) count += 1   // 查看按钮
      if (this.canEditDevice) count += 2   // 编辑 + 解绑按钮
      return count
    },
    // 是否有任何操作权限
    hasAnyActionPermission() {
      return this.visibleActionCount > 0
    },
    // 操作列动态宽度
    actionColumnWidth() {
      const count = this.visibleActionCount
      const widthMap = { 1: 100, 2: 170, 3: 240 }
      return widthMap[count] || 0
    }
  },
  created() {
    this.loadColumnWidths()
    this.fetchDeviceTypes()
    this.fetchPeripheralTypes()
    this.fetchDeviceList()
    this.fetchUserList()
    this.fetchFirmwareTypes()
    // 定时刷新设备列表，确保设备在线状态实时更新
    this._refreshTimer = setInterval(() => {
      this.fetchDeviceList()
    }, 60000)
  },
  beforeDestroy() {
    if (this._refreshTimer) {
      clearInterval(this._refreshTimer)
      this._refreshTimer = null
    }
  },
  methods: {
    hasPermission,
    // 加载保存的列宽
    loadColumnWidths() {
      const savedWidths = localStorage.getItem('device_list_column_widths')
      if (savedWidths) {
        try {
          this.columnWidths = JSON.parse(savedWidths)
        } catch (e) {
          console.error('解析列宽失败:', e)
          this.columnWidths = {}
        }
      }
    },

    // 监听列宽拖动结束
    handleHeaderDragend(newWidth, oldWidth, column) {
      if (column.property) {
        this.columnWidths[column.property] = newWidth
        localStorage.setItem('device_list_column_widths', JSON.stringify(this.columnWidths))
      }
    },

    // 获取列宽
    getColumnWidth(prop, defaultWidth) {
      return this.columnWidths[prop] || defaultWidth
    },

    // 获取产品类型字典
    async fetchDeviceTypes() {
      try {
        const data = await getDictDataByType('DEVICE_TYPE')
        if (data) {
          this.deviceTypes = data.data || data || []  
        }
      } catch (error) {
        console.error('获取产品种类失败:', error)
      }
    },
    // 获取周边类型字典
    async fetchPeripheralTypes() {
      try {
        const res = await getDictDataByType('PERIPHERAL_TYPE')
        const data = res.data || res
        if (data) {
          this.peripheralTypes = data
        }
      } catch (error) {
        console.error('获取周边类型失败:', error)
      }
    },
    // 获取固件类型字典
    async fetchFirmwareTypes() {
      try {
        const data = await getDictDataByType('FIRMWARE_TYPE')
        if (data) {
          this.firmwareTypes = data
        }
      } catch (error) {
        console.error('获取固件类型失败:', error)
      }
    },
    // fetchData别名，供全局mixin在activated时自动刷新
    fetchData() {
      this.fetchDeviceList()
    },
    // 获取设备列表
    async fetchDeviceList() {
      this.loading = true
      try {
        const params = {
          page: this.page,
          limit: this.limit,
          keywords: this.filterForm.keywords || undefined,
          deviceCode: this.filterForm.deviceCode || undefined,
          board: this.filterForm.board || undefined,
          productType: 'DEVICE',
          category: this.filterForm.category || undefined,
          status: this.filterForm.status !== '' ? this.filterForm.status : undefined
        }

        const data = await getDeviceList(params)

        // 确保数据结构正确
        if (data && data.list) {
          this.deviceList = data.list
          this.total = data.total || 0
        } else if (Array.isArray(data)) {
          this.deviceList = data
          this.total = data.length
        } else if (data && typeof data === 'object') {
          this.deviceList = data.records || data.data || []
          this.total = data.total || 0
        } else {
          this.deviceList = []
          this.total = 0
        }
      } catch (error) {
        console.error('获取设备列表失败:', error)
        this.$message.error('获取设备列表失败')
      } finally {
        this.loading = false
      }
    },

    // 获取用户列表（用于设备绑定）
    async fetchUserList() {
      const oldUserList = [...this.userList]
      try {
        const data = await getUserList({ limit: 1000 })
        const list = (data && data.list) || data || []
        const normalizedUsers = list
          .filter(user => user && String(user.status) !== '0')
          .map(user => ({
            ...user,
            id: String(user.userid || user.id)
          }))
        const uniqueUsers = []
        const seen = new Set()
        normalizedUsers.forEach(user => {
          if (!seen.has(user.id)) {
            seen.add(user.id)
            uniqueUsers.push(user)
          }
        })
        this.userList = uniqueUsers
      } catch (error) {
        console.group('Fetch User List Error (in DeviceList)')
        console.error('Time:', new Date().toLocaleString())
        console.error('Endpoint: /sys/user/list (via getUserList)')
        console.error('Params:', { limit: 1000 })
        console.error('Raw Error:', error)
        console.groupEnd()
        
        this.$message.error(this.$t('userManagement.fetchErrorDetailed'))
        this.userList = oldUserList
      }
    },

    // 搜索
    handleSearch() {
      this.page = 1
      this.fetchDeviceList()
    },

    // 设备编码输入防抖处理（300ms 延迟，避免高频请求影响性能）
    handleDeviceCodeInput: debounce(function() {
      this.page = 1
      this.fetchDeviceList()
    }, 300),

    // 重置
    handleReset() {
      this.filterForm = {
        keywords: '',
        deviceCode: '',
        board: '',
        status: '',
        category: '',
        productType: 'DEVICE'
      }
      this.page = 1
      this.fetchDeviceList()
    },

    // 刷新
    handleRefresh() {
      this.fetchDeviceList()
    },

    // 页面大小变化
    handleSizeChange(val) {
      this.limit = val
      this.page = 1
      this.fetchDeviceList()
    },

    // 页码变化
    handleCurrentChange(val) {
      this.page = val
      this.fetchDeviceList()
    },

    // 表格选择变化
    handleSelectionChange(selection) {
      this.selectedDevices = selection
    },

    handleBindDevice() {
      this.$router.push('/device/add')
    },

    // 编辑设备
    handleEditDevice(row) {
      this.$router.push(`/device/edit/${row.id}`)
    },

    // 查看设备
    handleViewDevice(row) {
      this.$router.push(`/device/detail/${row.id}`)
    },

    handleUnbindDevice(row) {
      this.$confirm(
        `确定要解绑设备"${row.deviceName}"吗？解绑后该设备将从列表中移除。`,
        this.$t('common.confirm'),
        {
          confirmButtonText: this.$t('common.confirm'),
          cancelButtonText: this.$t('common.cancel'),
          type: 'warning'
        }
      ).then(async () => {
        try {
          const deviceCode = row.deviceCode || row.device_code
          if (deviceCode) {
            await unbindDeviceByCode({ deviceCode, userId: row.userId || row.user_id })
          } else {
            this.$message.error('缺少设备编码，无法解绑')
            return
          }
          this.$message.success('设备解绑成功')
          await this.fetchDeviceList()
        } catch (error) {
          console.error('解绑设备失败:', error)
          const errorMsg = error.response && error.response.data && error.response.data.msg 
            ? error.response.data.msg 
            : (error.message || this.$t('device.unbindFailed'))
          this.$message.error(errorMsg)
        }
      }).catch(() => {
        this.$message.info(this.$t('device.unbindFailed'))
      })
    },

    // 批量操作
    handleBatchOperation() {
      this.batchOperationDialog.visible = true
      this.batchForm.operation = ''
      this.batchForm.userId = ''
    },

    // 执行批量操作
    async executeBatchOperation() {
      if (!this.batchForm.operation) {
        this.$message.warning(this.$t('device.selectOperation'))
        return
      }

      this.batchOperationDialog.loading = true
      try {
        const deviceIds = this.selectedDevices.map(device => device.id)
        await batchDeviceOperation(
          deviceIds,
          this.batchForm.operation,
          this.batchForm.userId ? { userId: this.batchForm.userId } : {}
        )

        this.$message.success(this.$t('common.operationSuccess'))
        this.batchOperationDialog.visible = false
        this.fetchDeviceList()
        this.selectedDevices = []
      } catch (error) {
        console.error('批量操作失败:', error)
        this.$message.error(this.$t('common.operationFailed'))
      } finally {
        this.batchOperationDialog.loading = false
      }
    },

    // 导出
    async handleExport() {
      this.exportLoading = true
      try {
        const params = {
          ...this.filterForm,
          export: true
        }
        await getDeviceList(params)
        this.$message.success(this.$t('common.exportSuccess'))
      } catch (error) {
        console.error('导出失败:', error)
        this.$message.error(this.$t('common.exportFailed'))
      } finally {
        this.exportLoading = false
      }
    },

    // 获取设备型号标签
    getDeviceModelLabel(type) {
      if (!type) return type;
      const firmwareType = this.firmwareTypes.find(item => item.key === type)
      return firmwareType ? firmwareType.name : type
    },



    getPeripheralTypeLabel(type) {
      if (!type) return type;
      const peripheralType = this.peripheralTypes.find(item => item.key === type)
      return peripheralType ? peripheralType.name : type
    },

    // 格式化日期
    formatDate(date) {
      if (!date) return ''
      return new Date(date).toLocaleString()
    },

    // 格式化日期时间（标准格式：年-月-日 时:分:秒）
    formatDateTime(date) {
      if (!date) return ''
      const d = new Date(date)
      const year = d.getFullYear()
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      const hours = String(d.getHours()).padStart(2, '0')
      const minutes = String(d.getMinutes()).padStart(2, '0')
      const seconds = String(d.getSeconds()).padStart(2, '0')
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
    },

    // 自动升级变化处理
    async handleAutoUpdateChange(row, val) {
      try {
        console.log('保存自动升级状态:', row.id, val)
        await updateDevice(row.id, { autoUpdate: val })
        this.$message.success(this.$t('common.saveSuccess'))
      } catch (error) {
        console.error('保存自动升级状态失败:', error)
        this.$message.error(this.$t('common.saveFailed'))
        // 恢复原状
        row.autoUpdate = val === 1 ? 0 : 1
      }
    },

    // 获取生命周期状态对应的标签类型
    getLifecycleType(status) {
      const types = {
        'created': 'info',
        'production': 'warning',
        'warehouse': 'primary',
        'shipped': 'success',
        'activated': 'success'
      }
      return types[status] || 'info'
    },

    // 获取生命周期状态对应的文本标签
    getLifecycleLabel(status) {
      if (!status) return '-'
      const labels = {
        'created': this.$t('device.lifecycleCreated'),
        'production': this.$t('device.lifecycleProduction'),
        'warehouse': this.$t('device.lifecycleWarehouse'),
        'shipped': this.$t('device.lifecycleShipped'),
        'activated': this.$t('device.lifecycleActivated')
      }
      return labels[status] || status
    }
  }
}
</script>

<style scoped lang="scss">
@import "@/styles/list-page.scss";

/* 设备列表页面特有样式 */
.list-page-container {
  .bind-username {
    font-weight: 500;
    color: #1e293b;
  }

  .list-card {
    :deep(.el-table) {
      &.custom-table {
        .action-buttons {
          min-height: 32px;
          width: 100%;
          flex-wrap: nowrap;

          .el-button--mini {
            padding: 7px 12px;
            font-weight: 500;
            transition: all 0.2s;

            &:hover {
              transform: translateY(-1px);
              box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
            }
          }
        }
      }
    }
  }
}
</style>
